import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChangePayerModal from './ChangePayerModal';
import './COBTimeline.css';

const ORDER_LABELS = { 1: 'Primary', 2: 'Secondary', 3: 'Tertiary' };

const ORDER_STYLE = {
  1: { color: '#171363', bg: '#eef0ff' },
  2: { color: '#0369a1', bg: '#e0f2fe' },
  3: { color: '#7c3aed', bg: '#f5f3ff' },
};

function fmtTimestamp(d) {
  if (!d) return null;
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmt(n) {
  return '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
}

// The balance a payer hands to the next COB tier (or the patient): the portion of what
// reached it that it didn't pay. Preference order:
//   1. The payer's explicitly reported patient responsibility (PR adjustments).
//   2. Its own allowed − paid (the complete-ERA path: allowed already nets out
//      contractual write-offs, so allowed − paid is the leftover responsibility).
//   3. A running-balance fallback — what flowed IN from the prior payer, less what this
//      payer paid and adjusted away (contractual, plus any OA/PI amount — Step 13). This covers sparse ERAs (common in the
//      Claim.MD sandbox) where a secondary/tertiary 835 comes back with allowed = 0 and
//      no PR line, which would otherwise collapse the forwarded balance to $0.
// `balanceIn` is the amount forwarded into this payer (null for the primary). Returns
// null when there is no remit to forward from.
function forwardedBalance(remit, balanceIn = null) {
  if (!remit) return null;
  const paid = parseFloat(remit.paid_amount || 0);
  const pr = parseFloat(remit.patient_responsibility || 0);
  if (pr > 0) return pr;
  const allowed = parseFloat(remit.allowed_amount || 0);
  if (allowed > 0) return Math.max(0, allowed - paid);
  if (balanceIn != null) {
    const contractual = parseFloat(remit.contractual_adj || 0);
    const other = parseFloat(remit.other_adj || 0);
    return Math.max(0, balanceIn - paid - contractual - other);
  }
  return 0;
}

// Build timeline events for the primary payer slot
function primaryEvents(claim, remit) {
  const events = [
    { label: 'Claim Created', date: claim.created_at, done: true },
  ];

  if (['generated', 'pending', 'accepted', 'rejected', 'voided'].includes(claim.claim_status)) {
    events.push({ label: 'Claim Generated', date: claim.generated_at, done: true });
  }

  const primarySubmitted =
    ['submitted', 'denied', 'paid'].includes(claim.status) ||
    !!claim.submitted_at ||
    !!remit?.submitted_date;

  if (primarySubmitted) {
    events.push({
      label: 'Submitted to Primary Payer',
      date: claim.submitted_at || remit?.submitted_date,
      done: true,
    });
  }

  if (claim.void_submitted_at) {
    events.push({ label: 'Withdrawn from Primary Payer (void sent)', date: claim.void_submitted_at, done: true });
  }

  if (remit?.remit_date) {
    events.push({ label: 'Remit Received', date: remit.remit_date, dateOnly: true, done: true });
  }

  // Internal void — claim marked dead in our system (no payer transmission; the
  // 837 freq-8 case above already records its own "void sent" event).
  if (claim.claim_status === 'voided' && !claim.void_submitted_at) {
    events.push(voidEvent(claim));
  }

  return events;
}

// A "Claim Voided" timeline event, capturing where in the process the void happened
// (the status it was voided from) and the biller's reason.
function voidEvent(node) {
  return {
    label: node.voided_from_status
      ? `Claim Voided (was ${node.voided_from_status})`
      : 'Claim Voided',
    date: node.voided_at,
    reason: node.void_reason,
    voided: true,
    done: true,
  };
}

// Build timeline events for secondary / tertiary payer slots
function payerEvents(order, remit, linkedClaim) {
  const events = [];
  const submittedDate = linkedClaim?.submitted_at || remit?.submitted_date;
  if (submittedDate) {
    events.push({
      label: `Submitted to ${ORDER_LABELS[order]} Payer`,
      date: submittedDate,
      done: true,
    });
  }
  if (linkedClaim?.void_submitted_at) {
    events.push({ label: `Withdrawn from ${ORDER_LABELS[order]} Payer (void sent)`, date: linkedClaim.void_submitted_at, done: true });
  }
  if (remit?.remit_date) {
    events.push({ label: 'Remit Received', date: remit.remit_date, dateOnly: true, done: true });
  }
  if (linkedClaim?.claim_status === 'voided' && !linkedClaim.void_submitted_at) {
    events.push(voidEvent(linkedClaim));
  }
  return events;
}

function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function VoidIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function PayerSection({ order, payer, remit, prevRemit, balanceIn, claim, linkedClaim, hasNextPayer, remitFinalized, onAddRemit, onEditRemit, onGenerateClaim, onChangePayer, onViewRebill }) {
  const label    = ORDER_LABELS[order];
  const style    = ORDER_STYLE[order];
  const isWaiting = order > 1 && !prevRemit?.remit_date;

  const isSubmitted =
    order === 1
      ? ['submitted', 'denied', 'paid'].includes(claim.status) || !!remit?.submitted_date
      : !!linkedClaim; // secondary/tertiary: a linked child claim exists = generated

  const hasRemit       = !!remit;
  const remitProcessed = !!remit?.remit_date;
  const prevLabel = ORDER_LABELS[order - 1];

  // Balance this payer owes to the next slot — see forwardedBalance. Passing balanceIn
  // lets a tier with a sparse ERA still forward the running remainder instead of $0.
  const balanceFwd = hasRemit ? forwardedBalance(remit, balanceIn) : null;

  const events = order === 1 ? primaryEvents(claim, remit) : payerEvents(order, remit, linkedClaim);

  return (
    <div className={`cob-section${isWaiting ? ' cob-section--waiting' : ''}`}>
      {/* Payer header */}
      <div className="cob-section-header">
        <div className="cob-section-header-left">
          <span className="cob-order-badge" style={{ color: style.color, background: style.bg }}>
            {label}
          </span>
          <div className="cob-payer-info">
            <span className="cob-payer-name">
              {payer?.payer_name || payer?.insurance_provider || `${label} Insurance`}
            </span>
            {(payer?.insured_id || payer?.policy_number) && (
              <span className="cob-payer-meta">
                ID: {payer.insured_id || payer.policy_number}
              </span>
            )}
            {order > 1 && linkedClaim?.claim_number && (
              <span className="cob-payer-meta">Claim #{linkedClaim.claim_number}</span>
            )}
          </div>
        </div>

        <div className="cob-section-actions">
          {onChangePayer && (
            <button className="cob-btn cob-btn--change" onClick={onChangePayer} title="Reassign the payer billed at this level">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Change Payer
            </button>
          )}
          {/* Superseded level — route the biller to the newer claim instead of a dead button. */}
          {onViewRebill && (
            <span className="cob-rebilled-note">
              Rebilled to a newer claim —{' '}
              <button type="button" className="cob-rebilled-note-link" onClick={onViewRebill}>
                change the payer there
              </button>
            </span>
          )}
          {/* Primary: submit tracking + remit buttons */}
          {order === 1 && isSubmitted && !hasRemit && (
            <button className="cob-btn cob-btn--remit" onClick={onAddRemit}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Record Remit
            </button>
          )}
          {order === 1 && hasRemit && (
            <button className="cob-btn cob-btn--edit" onClick={onEditRemit}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              {remitFinalized ? 'View Remit' : 'Edit Remit'}
            </button>
          )}

          {/* Secondary/Tertiary: generate or record remit */}
          {order > 1 && !isWaiting && !isSubmitted && (
            <button className="cob-btn cob-btn--generate" onClick={onGenerateClaim}>
              Generate {label} Claim
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
          {order > 1 && isSubmitted && !hasRemit && (
            <button className="cob-btn cob-btn--remit" onClick={onAddRemit}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Record Remit
            </button>
          )}
          {order > 1 && hasRemit && (
            <button className="cob-btn cob-btn--edit" onClick={onEditRemit}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              {remitFinalized ? 'View Remit' : 'Edit Remit'}
            </button>
          )}
        </div>
      </div>

      {/* Balance forwarded from previous payer */}
      {order > 1 && prevRemit?.remit_date && (
        <div className="cob-balance-fwd">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          Balance forwarded from {prevLabel}:&nbsp;
          {/* balanceIn IS the amount the prior payer forwarded into this tier (computed as
              a running balance in the parent), so display it directly for consistency. */}
          <strong>{fmt(balanceIn || 0)}</strong>
        </div>
      )}

      {isWaiting ? (
        <div className="cob-waiting">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          Waiting for {prevLabel} remit before {label.toLowerCase()} billing can proceed
        </div>
      ) : (
        <>
          {/* Timeline events */}
          {events.length > 0 && (
            <div className="cob-events">
              {events.map((evt, i) => (
                <div key={i} className={`cob-event${evt.voided ? ' cob-event--voided' : ''}`}>
                  <div className={`cob-event-dot${evt.done ? ' cob-event-dot--done' : ''}${evt.voided ? ' cob-event-dot--voided' : ''}`}>
                    {evt.voided ? <VoidIcon /> : evt.done && <CheckIcon />}
                  </div>
                  {i < events.length - 1 && <div className="cob-event-line" />}
                  <div className="cob-event-content">
                    <span className="cob-event-label">{evt.label}</span>
                    {evt.date && <span className="cob-event-date">{evt.dateOnly ? fmtDate(evt.date) : fmtTimestamp(evt.date)}</span>}
                    {evt.reason && <span className="cob-event-reason">Reason: {evt.reason}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Prompt when ready but not yet submitted */}
          {events.length === 0 && (
            <div className="cob-ready">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Ready to generate {label.toLowerCase()} claim
            </div>
          )}

          {/* Remit details */}
          {hasRemit && (
            <div className="cob-remit-card">
              {!remitProcessed && (
                <div className="cob-remit-pending">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Remit date not yet entered — add it to unlock{order < 3 ? ` ${ORDER_LABELS[order + 1].toLowerCase()} billing` : ' final billing'}.
                </div>
              )}
              <div className="cob-remit-rows">
                <div className="cob-remit-row">
                  <span className="cob-remit-label">Billed</span>
                  <span className="cob-remit-value">{fmt(remit.billed_amount)}</span>
                </div>
                <div className="cob-remit-row">
                  <span className="cob-remit-label">Allowed</span>
                  <span className="cob-remit-value">{fmt(remit.allowed_amount)}</span>
                </div>
                {parseFloat(remit.contractual_adj || 0) > 0 && (
                  <div className="cob-remit-row">
                    <span className="cob-remit-label">Contractual Adj.</span>
                    <span className="cob-remit-value cob-remit-value--adj">
                      − {fmt(remit.contractual_adj)}
                    </span>
                  </div>
                )}
                {parseFloat(remit.other_adj || 0) > 0 && (
                  <div className="cob-remit-row">
                    <span className="cob-remit-label">Other Adj. (OA/PI)</span>
                    <span className="cob-remit-value cob-remit-value--adj">
                      − {fmt(remit.other_adj)}
                    </span>
                  </div>
                )}
                <div className="cob-remit-row cob-remit-row--paid">
                  <span className="cob-remit-label">Paid</span>
                  <span className="cob-remit-value cob-remit-value--paid">{fmt(remit.paid_amount)}</span>
                </div>
                {parseFloat(remit.patient_responsibility || 0) > 0 && (
                  <div className="cob-remit-row">
                    <span className="cob-remit-label">Patient Resp.</span>
                    <span className="cob-remit-value">{fmt(remit.patient_responsibility)}</span>
                  </div>
                )}
                {remit.adjustment_codes && (
                  <div className="cob-remit-row">
                    <span className="cob-remit-label">Adj. Codes</span>
                    <span className="cob-remit-value cob-remit-value--codes">{remit.adjustment_codes}</span>
                  </div>
                )}
                {remit.denial_reason && (
                  <div className="cob-remit-row cob-remit-row--denial">
                    <span className="cob-remit-label">Denial Reason</span>
                    <span className="cob-remit-value">{remit.denial_reason}</span>
                  </div>
                )}
                {remit.check_number && (
                  <div className="cob-remit-row">
                    <span className="cob-remit-label">Check / EFT #</span>
                    <span className="cob-remit-value">{remit.check_number}</span>
                  </div>
                )}
              </div>

              {/* Balance forward to next payer or patient */}
              {balanceFwd !== null && balanceFwd > 0 && (
                hasNextPayer ? (
                  <div className="cob-remit-balance">
                    <span>Balance to {ORDER_LABELS[order + 1]}</span>
                    <strong>{fmt(balanceFwd)}</strong>
                  </div>
                ) : (
                  <div className="cob-remit-balance cob-remit-balance--patient">
                    <div className="cob-remit-balance-row">
                      <span>Patient Responsibility</span>
                      <strong>{fmt(balanceFwd)}</strong>
                    </div>
                    {order < 3 && (
                      <p className="cob-remit-balance-hint">
                        Add a {ORDER_LABELS[order + 1].toLowerCase()} payer to the patient profile to bill their {ORDER_LABELS[order + 1].toLowerCase()} insurance.
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function COBTimeline({ claim, payers, remits, linkedClaims = [], onRemitSaved }) {
  const navigate = useNavigate();
  const [changePayer, setChangePayer] = useState(null); // { order, claimAtLevel, currentPayer }

  const handleGenerateClaim = (order, payer, prevRemit) => {
    navigate('/worklist/generate', {
      state: {
        claim,
        isEdit:        false,
        cobMode:       true,
        cobOrder:      order,
        cobPayer:      payer,
        cobPriorRemit: prevRemit,
        returnTo:      `/claims/${claim.id}`,
      },
    });
  };

  // Navigate to /revenue/payments and open modal for a new remit pre-populated with this claim
  const handleAddRemit = (cobClaimId, order, payer, billedAmount, submittedAt, prevRemit) => {
    const params = new URLSearchParams({
      open:        '1',
      mode:        'single',
      claimId:     cobClaimId,
      payerOrder:  order,
      payerName:   payer?.payer_name || payer?.insurance_provider || '',
      ...(payer?.insurance_payer_id ? { payerId: payer.insurance_payer_id } : {}),
      ...(billedAmount != null       ? { billedAmt: billedAmount }           : {}),
      ...(submittedAt                ? { submittedAt }                       : {}),
      // For secondary/tertiary: pass the previous payer's remit data so the modal
      // can display the forwarded-balance context without an extra API call.
      ...(order > 1 && prevRemit ? {
        prevPayerName:       prevRemit.payer_name            || '',
        prevBilledAmt:       prevRemit.billed_amount         ?? '',
        prevAllowedAmt:      prevRemit.allowed_amount        ?? '',
        prevPaidAmt:         prevRemit.paid_amount           ?? '',
        prevContractualAdj:  prevRemit.contractual_adj       ?? '',
        prevOtherAdj:        prevRemit.other_adj             ?? '',
        prevPatientResp:     prevRemit.patient_responsibility ?? '',
      } : {}),
    });
    navigate(`/revenue/payments?${params.toString()}`);
  };

  // Navigate to /revenue/payments to edit/view an existing remit
  const handleEditRemit = (remit, isFinalized) => {
    if (remit?.remit_batch_id) {
      // Linked to a batch — open that batch (read-only if finalized)
      const params = new URLSearchParams({
        open:    '1',
        batchId: remit.remit_batch_id,
        ...(isFinalized ? { readonly: '1' } : {}),
      });
      navigate(`/revenue/payments?${params.toString()}`);
    } else {
      // Legacy remit not linked to a batch — open in single-claim edit mode
      const params = new URLSearchParams({
        open:    '1',
        mode:    'single',
        remitId: remit.claim_remit_id,
      });
      navigate(`/revenue/payments?${params.toString()}`);
    }
  };

  // At minimum, always show primary; show secondary/tertiary if data exists
  const activePayers = payers.length > 0
    ? payers
    : [{ payer_order: 1, payer_name: null }];

  const primaryRemit = remits.find((r) => r.payer_order === 1) ?? null;

  const remitForOrder = (order) =>
    order === 1 ? primaryRemit : (linkedClaims.find((lc) => lc.cob_order === order)?.remit ?? null);

  // Running COB balance: each tier receives what the previous tier forwarded. Computed
  // once across the ordered tiers so a tier whose ERA is sparse (allowed 0 / no PR line)
  // still forwards the correct remaining balance (in − paid − write-off) to the next tier
  // instead of dropping it to $0. balanceInByOrder[o] = amount forwarded INTO tier o.
  const balanceInByOrder = {};
  {
    let carried = null; // nothing is forwarded into the primary
    for (const p of [...activePayers].sort((a, b) => a.payer_order - b.payer_order)) {
      balanceInByOrder[p.payer_order] = carried;
      const fwd = forwardedBalance(remitForOrder(p.payer_order), carried);
      if (fwd != null) carried = fwd;
    }
  }

  return (
    <div className="cob-timeline">
      {activePayers.map((payer) => {
        const order = payer.payer_order;

        const linkedClaim = order > 1
          ? (linkedClaims.find((lc) => lc.cob_order === order) ?? null)
          : null;

        // A secondary/tertiary claim stores its billed payer at payer_order=1 on its own
        // child record (surfaced as linkedClaim.payer), not in the primary claim's COB
        // snapshot. Prefer the child's payer so a payer change on the child is reflected;
        // fall back to the primary's snapshot before the child claim has been generated.
        const sectionPayer = order > 1 ? (linkedClaim?.payer ?? payer) : payer;

        const remit = order === 1
          ? primaryRemit
          : (linkedClaim?.remit ?? null);

        const prevRemit = order > 1
          ? (order === 2
              ? primaryRemit
              : (linkedClaims.find((lc) => lc.cob_order === order - 1)?.remit ?? null))
          : null;

        const cobClaimId = order === 1 ? claim.id : (linkedClaim?.id ?? null);
        const hasNextPayer = payers.some((p) => p.payer_order === order + 1);

        // What the next payer is expected to consider = the balance forwarded into this
        // tier (running balance), so recording its remit pre-fills with the right figure.
        const balanceIn = balanceInByOrder[order] ?? null;
        const modalBilledAmount = order > 1 && prevRemit
          ? (balanceIn ?? claim.amount)
          : claim.amount;

        const submittedAt = order === 1 ? claim.submitted_at : linkedClaim?.submitted_at;
        const remitFinalized = remit?.remit_batch_status === 'finalized';

        // The claim record at THIS level: root for primary, the COB child otherwise.
        const claimAtLevel = order === 1 ? claim : linkedClaim;
        // A superseded claim (already rebilled to a new payer) is frozen — you rebill the
        // newer claim at the tip of the chain, not this one. The backend rejects it too;
        // this just keeps the UI from offering an action that would 409. (COB children are
        // already filtered server-side, so in practice this guards the primary/root level.)
        const supersededToId = claimAtLevel?.rebilled_to_claim_id ?? null;
        const canChangePayer = !!cobClaimId && claimAtLevel?.claim_status !== 'voided' && !supersededToId;

        return (
          <PayerSection
            key={order}
            order={order}
            payer={sectionPayer}
            remit={remit}
            prevRemit={prevRemit}
            balanceIn={balanceIn}
            claim={claim}
            linkedClaim={linkedClaim}
            hasNextPayer={hasNextPayer}
            remitFinalized={remitFinalized}
            onAddRemit={cobClaimId ? () => handleAddRemit(cobClaimId, order, sectionPayer, modalBilledAmount, submittedAt, prevRemit) : null}
            onEditRemit={remit ? () => handleEditRemit(remit, remitFinalized) : null}
            onGenerateClaim={() => handleGenerateClaim(order, sectionPayer, prevRemit ? { ...prevRemit, forwarded_balance: balanceIn } : prevRemit)}
            onChangePayer={canChangePayer ? () => setChangePayer({ order, claimAtLevel, currentPayer: sectionPayer }) : null}
            onViewRebill={supersededToId ? () => navigate(`/claims/${supersededToId}`) : null}
          />
        );
      })}

      {changePayer && (
        <ChangePayerModal
          claimAtLevel={changePayer.claimAtLevel}
          order={changePayer.order}
          patientId={claim.patient_id}
          currentPayer={changePayer.currentPayer}
          onClose={() => setChangePayer(null)}
          onChanged={() => onRemitSaved?.()}
        />
      )}
    </div>
  );
}
